/**
 * User Management Dialogs
 * Contains ChangeRoleDialog, ActivityLogDialog, and BulkApproveDialog
 */

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CheckCircle, Shield, History, Mail } from 'lucide-react';
import { UserProfile, Role, UserManagementLog } from '../../lib/api';

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

// --- Change Role Dialog ---
interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  availableRoles: Role[];
  selectedRoleId: number | null;
  setSelectedRoleId: (id: number | null) => void;
  actionLoading: boolean;
  onChangeRole: () => void;
}

export function ChangeRoleDialog({
  open,
  onOpenChange,
  selectedUser,
  availableRoles,
  selectedRoleId,
  setSelectedRoleId,
  actionLoading,
  onChangeRole,
}: ChangeRoleDialogProps) {
  const compatibleRoles = selectedUser
    ? availableRoles.filter((r) => r.company_type === selectedUser.company_type)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change User Role</DialogTitle>
          <DialogDescription>
            Select a new role for {selectedUser?.user.full_name}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label className="text-sm font-medium mb-2 block">Current Role</Label>
          <Badge variant="outline" className="mb-4">{selectedUser?.role_name}</Badge>

          <Label className="text-sm font-medium mb-2 block">New Role</Label>
          <Select
            value={selectedRoleId?.toString() || ''}
            onValueChange={(value) => setSelectedRoleId(parseInt(value))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {compatibleRoles.map((role) => (
                <SelectItem key={role.id} value={role.id.toString()}>
                  <div className="flex items-center gap-2">
                    <span>{role.name}</span>
                    {role.is_admin_role && (
                      <Badge variant="secondary" className="text-xs">Admin</Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedRoleId && compatibleRoles.find(r => r.id === selectedRoleId) && (
            <div className="mt-4 p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                {compatibleRoles.find(r => r.id === selectedRoleId)?.description}
              </p>
              <div className="mt-2 text-xs text-muted-foreground">
                {compatibleRoles.find(r => r.id === selectedRoleId)?.permissions_summary}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setSelectedRoleId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onChangeRole}
            disabled={actionLoading || !selectedRoleId || selectedRoleId === selectedUser?.role}
          >
            <Shield className="h-4 w-4 mr-2" />
            Change Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Activity Log Dialog ---
interface ActivityLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  activityLogs: UserManagementLog[];
}

export function ActivityLogDialog({
  open,
  onOpenChange,
  selectedUser,
  activityLogs,
}: ActivityLogDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>User Activity Log</DialogTitle>
          <DialogDescription>
            Management actions performed on {selectedUser?.user.full_name}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto py-4">
          {activityLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No activity logs found for this user.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activityLogs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant={
                      log.action.includes('enabled') ? 'default' :
                      log.action.includes('disabled') ? 'secondary' :
                      'outline'
                    }>
                      {log.action_display}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm mb-2">{log.description}</p>
                  <div className="text-xs text-muted-foreground">
                    By: {log.performed_by_name} ({log.performed_by_email})
                  </div>
                  {(Object.keys(log.old_value).length > 0 || Object.keys(log.new_value).length > 0) && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      {Object.keys(log.old_value).length > 0 && (
                        <div className="bg-red-50 p-2 rounded">
                          <span className="font-medium">Before:</span>
                          <pre className="mt-1 text-red-700">
                            {JSON.stringify(log.old_value, null, 2)}
                          </pre>
                        </div>
                      )}
                      {Object.keys(log.new_value).length > 0 && (
                        <div className="bg-green-50 p-2 rounded">
                          <span className="font-medium">After:</span>
                          <pre className="mt-1 text-green-700">
                            {JSON.stringify(log.new_value, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bulk Approve Dialog ---
interface BulkApproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  pendingInSelectionCount: number;
  actionLoading: boolean;
  onBulkApprove: () => void;
}

export function BulkApproveDialog({
  open,
  onOpenChange,
  selectedCount,
  pendingInSelectionCount,
  actionLoading,
  onBulkApprove,
}: BulkApproveDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve selected users</DialogTitle>
          <DialogDescription>
            {selectedCount} user(s) selected. Only users pending approval will be approved
            ({pendingInSelectionCount} in this selection). Already-approved users are skipped.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onBulkApprove}
            disabled={actionLoading || pendingInSelectionCount === 0}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve pending ({pendingInSelectionCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bulk send invitation emails ---
interface BulkSendInvitationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  actionLoading: boolean;
  onConfirm: () => void;
}

export function BulkSendInvitationsDialog({
  open,
  onOpenChange,
  selectedCount,
  actionLoading,
  onConfirm,
}: BulkSendInvitationsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send invitation emails</DialogTitle>
          <DialogDescription>
            Send an invitation email to each of the {selectedCount} selected user(s).
            You can resend to users who were already invited.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={actionLoading || selectedCount === 0}>
            <Mail className="h-4 w-4 mr-2" />
            Send {selectedCount} email(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
